import PropTypes from 'prop-types';
import React, { createContext, useEffect, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getUserByFirebaseId, googleLogin, registerHomeowner } from '../api';
import { readReferralCode, clearReferralCode } from '../utils/referral';
import { servicePro } from '../utils/role';
import { firebase } from './firebase-config';
import webServices from '../services/webServices';
import track, { ONBOARDING_EVENTS } from '../utils/onboardingAnalytics';

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  resiviewUser: null,
  // undefined = not resolved yet. RoleBasedGuard needs to tell "still loading"
  // apart from "no profile", or a deep link flashes Permission Denied at a user
  // who has permission.
  resiviewUserResolved: false
};

const reducer = (state, action) => {
  if (action.type === 'INITIALISE') {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  }

  return state;
};

const AuthContext = createContext({
  ...initialState,
  method: 'firebase',
  login: () => Promise.resolve(),
  register: () => Promise.resolve(),
  loginWithGoogle: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  resendSignUpMail: () => Promise.resolve()
});

AuthProvider.propTypes = {
  children: PropTypes.node
};
export function getUserUID() {
  return firebase.auth().currentUser.uid;
}
function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [resiviewUser, setResiviewUser] = useState(null);
  // Distinguishes "the profile fetch has not come back yet" from "there is no
  // profile". Without it RoleBasedGuard cannot avoid denying during the gap.
  const [resiviewUserResolved, setResiviewUserResolved] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const { currentUser } = firebase.auth();
    if (!currentUser) return;
    const firebaseId = currentUser.uid;
    getUserByFirebaseId({ firebaseId })
      .then(setResiviewUser)
      // Resolved either way. A failed lookup must not leave the app spinning
      // forever — it has to fall through to the same denial an absent profile
      // gets, just not before the answer is known.
      .finally(() => setResiviewUserResolved(true));
  }, []);

  useEffect(
    () =>
      firebase.auth().onAuthStateChanged((user) => {
        if (user && user.emailVerified) {
          user.getIdTokenResult().then((res) => {
            user.claims = res.claims;
            user.signInProvider = res.signInProvider;
            if (typeof window !== 'undefined' && !localStorage.getItem('walkthroughStatus'))
              localStorage.setItem('walkthroughStatus', res.claims.walkthroughStatus);
            if (typeof window !== 'undefined' && !localStorage.getItem('phone_number'))
              localStorage.setItem('phone_number', res.claims.phone_number);
            getUserByFirebaseId({ firebaseId: user.uid })
              .then((resolved) => {
                if (resolved) setResiviewUser(resolved);
              })
              .finally(() => setResiviewUserResolved(true));
            dispatch({
              type: 'INITIALISE',
              payload: { isAuthenticated: true, user }
            });
          });
        } else {
          dispatch({
            type: 'INITIALISE',
            payload: { isAuthenticated: false, user: null }
          });
        }
      }),
    [dispatch]
  );

  // Re-fetch the ResiView (DB) user record and update context. Used after
  // mutations such as accepting updated legal policies.
  const refreshResiviewUser = async () => {
    const { currentUser } = firebase.auth();
    if (!currentUser) return null;
    const user = await getUserByFirebaseId({ firebaseId: currentUser.uid });
    if (user) setResiviewUser(user);
    return user;
  };

  const login = (email, password) => firebase.auth().signInWithEmailAndPassword(email, password);

  const loginWithGoogle = async () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    const result = await firebase.auth().signInWithPopup(provider);
    const user = {
      email: result.user.email,
      firebaseId: result.user.uid,
      firstName: '',
      lastName: '',
      displayName: result.user.displayName,
      phoneNumber: result.user.phoneNumber || ''
    };
    await googleLogin(user);
    getUserByFirebaseId({ firebaseId: user.firebaseId }).then(setResiviewUser);
    return result;
  };

  const register = (email, password, firstName, lastName, phone) =>
    firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then(async (res) => {
        const UID = res.user.uid;
        await registerHomeowner({
          email,
          firebaseId: UID,
          firstName,
          lastName,
          displayName: `${firstName} ${lastName}`,
          phoneNumber: phone,
          referralCode: readReferralCode()
        });
        // Attribution is one-time: the code is consumed here so a second account
        // from the same browser is not credited to the same referrer.
        clearReferralCode();
        track(ONBOARDING_EVENTS.SIGNUP_COMPLETED);
        firebase.auth().signOut();
      });

  const logout = async () => {
    localStorage.clear();
    await firebase.auth().signOut();
  };

  const resetPassword = async (email) => {
    await firebase.auth().sendPasswordResetEmail(email);
  };
  const resendSignUpMail = async (email) => {
    await webServices.sendSignUpMail({
      email
    });
  };

  const auth = { ...state.user };
  // Cached walkthrough flag. Parsed defensively: the value is written from a
  // Firebase custom claim that may be absent, in which case localStorage stores
  // the literal string "undefined" — and anything unparseable here would throw
  // inside a render that wraps the entire authenticated app.
  //
  // FALSE IS THE SAFE DEFAULT: it sends somebody to the walkthrough they can
  // skip, rather than past a setup step they never did.
  const walkthroughStatus = typeof window !== 'undefined' ? localStorage.getItem('walkthroughStatus') : null;
  let completedWalkthrough = false;
  try {
    completedWalkthrough = walkthroughStatus ? JSON.parse(walkthroughStatus) === true : false;
  } catch (err) {
    completedWalkthrough = false;
  }
  return (
    <>
      <AuthContext.Provider
        value={{
          ...state,
          method: 'firebase',
          user: {
            id: auth.uid,
            email: auth.email,
            photoURL: auth.photoURL,
            displayName: auth.displayName || auth.email,
            role: auth?.claims?.role || 'Homeowner',
            phoneNumber: auth.phoneNumber,
            isPublic: auth?.isPublic || false,
            authProvider: auth?.signInProvider || '',
            completed_walkthrough: completedWalkthrough,
            isServicePro: pathname.includes('service-pro') || servicePro().includes(auth?.claims?.role)
          },
          resiviewUser,
          resiviewUserResolved,
          refreshResiviewUser,
          login,
          register,
          loginWithGoogle,
          logout,
          resetPassword,
          resendSignUpMail
        }}
      >
        {children}
      </AuthContext.Provider>
    </>
  );
}

export { AuthContext, AuthProvider };
